import { useMemo, useState } from 'react'
import { Check, ChevronsUpDown } from 'lucide-react'

import { cn } from '@/utils'
import { Popover, PopoverTrigger } from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { useFontsOptional } from '@/providers/font'

import { TRIGGER_CLASS } from '../constants/format-control-classes'
import { mergeFontFamilies, resolveVariantInFamily } from '../helpers/font-families'
import type { ControlProps } from '../types/control-props'
import { FormatPopoverContent } from './format-popover-content'

export function TextOnPathFontFamilyControl({
  config,
  onConfigChange,
  onRestoreFocus,
  className,
}: ControlProps) {
  const fonts = useFontsOptional()
  const [open, setOpen] = useState(false)
  const usedFamilies = fonts?.usedFamilies
  const allFamilies = fonts?.allFamilies

  const families = useMemo(
    () => mergeFontFamilies(usedFamilies, allFamilies),
    [usedFamilies, allFamilies]
  )

  const currentName =
    fonts?.getFamilyById(config.fontFamilyId)?.displayName ??
    fonts?.getFamilyCssName(config.fontFamilyId) ??
    'Font family'

  const handleSelect = (nextFamilyId: number) => {
    setOpen(false)
    // Without this the preview keeps painting in the fallback face and the control reads as broken.
    void fonts?.ensureFamilyFacesLoaded(nextFamilyId)

    const currentStyleName = fonts?.getVariantByFontId(config.fontId)?.fontStyleName
    const targetFonts = fonts?.getFamilyById(nextFamilyId)?.fonts ?? []
    const nextFontId =
      resolveVariantInFamily(targetFonts, currentStyleName) ??
      fonts?.pickDefaultFontIdForFamilyId(nextFamilyId)

    // Patch both halves together: a `fontId` left pointing into the previous family resolves to
    // that family's face, and the family change appears not to have taken.
    onConfigChange({
      fontFamilyId: nextFamilyId,
      ...(nextFontId != null ? { fontId: nextFontId } : {}),
    })
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        // The full catalog is lazy — without this the list only ever shows the spread's own fonts.
        if (next) fonts?.loadFullCatalog()
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={fonts == null}
          aria-label="Font family"
          className={cn(TRIGGER_CLASS, 'max-w-[9rem]', className)}
        >
          <span className="truncate">{currentName}</span>
          <ChevronsUpDown className="h-4 w-4 shrink-0" />
        </button>
      </PopoverTrigger>
      <FormatPopoverContent className="w-[260px] p-0" onRestoreFocus={onRestoreFocus}>
        <Command>
          <CommandInput placeholder="Search fonts..." className="h-9" />
          <CommandList>
            <CommandEmpty>
              {fonts?.isFullCatalogLoading ? 'Loading fonts...' : 'No font found.'}
            </CommandEmpty>
            <CommandGroup>
              {families.map((family) => (
                <CommandItem
                  key={family.fontFamilyId}
                  value={family.displayName}
                  onSelect={() => handleSelect(family.fontFamilyId)}
                  className="text-xs"
                >
                  <Check
                    className={cn(
                      'mr-2 h-3.5 w-3.5',
                      family.fontFamilyId === config.fontFamilyId ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  {family.displayName}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </FormatPopoverContent>
    </Popover>
  )
}
